import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion'; // Keep this line commented out or remove it
import WebsitePreview from '../components/WebsitePreview';
import SEO from '../components/SEO';
import SchemaMarkup from '../components/SchemaMarkup';
import { InstagramEmbed } from 'react-social-media-embed';

// Import logos of clients with real images
const clientLogos = [
  { id: 1, name: 'FanDuel', src: '/images/MLB.jpg' },
  { id: 2, name: 'MLB', src: '/images/Phantoms TB toss.jpg' },
  { id: 3, name: 'Pocono Raceway', src: '/images/pat hit disc - frame at 0m7s.jpg' },
  { id: 4, name: 'Real Estate', src: '/images/patio+(1+of+1).jpg' },
  { id: 5, name: 'Weddings', src: '/images/wedding+2+.jpg' },
  { id: 6, name: 'Wine Tours', src: '/images/Wine.jpg' },
];

const Home = () => {
  // Function to handle external link opening
  const openExternalLink = (url) => {
    window.open(url, '_blank');
  };

  return (
    <div className="home-page overflow-hidden">
      <SEO 
        title="Cosmik Drones: Premier Drone Services in Lehigh Valley, PA | FAA Certified"
        description="Cosmik Drones: Lehigh Valley's FAA-certified drone specialist for stunning aerial photography & FPV filming. Proud member of the Lehigh Valley Chamber of Commerce. Serving PA for real estate, events & more."
        canonicalUrl="https://cosmikdrones.com"
      />
      
      <SchemaMarkup 
        type="LocalBusiness"
        data={{
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": "Cosmik Drones",
          "image": "https://cosmikdrones.com/Cosmik%20Logo/Kolonia_Logo.png", // Assuming your logo is accessible at this URL
          "url": "https://cosmikdrones.com",
          "telephone": "610-509-0867",
          "priceRange": "$$",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "", // Add street address if you want it public
            "addressLocality": "Bethlehem",
            "addressRegion": "PA",
            "postalCode": "", // Add postal code
            "addressCountry": "US"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": "40.6084", // Existing latitude
            "longitude": "-75.4907" // Existing longitude
          },
          "areaServed": [
            {
              "@type": "GeoCircle",
              "geoMidpoint": {
                "@type": "GeoCoordinates",
                "latitude": "40.6084",
                "longitude": "-75.4907"
              },
              "geoRadius": "100000" // Approx 62 miles, adjust as needed
            },
            {
              "@type": "AdministrativeArea",
              "name": "Lehigh Valley"
            },
            {
              "@type": "State",
              "name": "Pennsylvania"
            }
          ],
          "openingHoursSpecification": {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": [
              "Monday",
              "Tuesday",
              "Wednesday",
              "Thursday",
              "Friday"
            ],
            "opens": "09:00",
            "closes": "17:00"
          },
          "memberOf": {
            "@type": "Organization",
            "name": "Greater Lehigh Valley Chamber of Commerce",
            "url": "https://www.lehighvalleychamber.org/" // Main Chamber URL
          },
          "sameAs": [ 
            "https://www.instagram.com/josh.kolonia/",
            "https://www.facebook.com/josh.kolonia",
            "https://www.youtube.com/channel/cosmikdrones",
            "https://www.linkedin.com/company/cosmik-drones",
            "https://web.lehighvalleychamber.org/Video-Production-and-Drone-Services/Cosmik-Drones-17765"
          ]
        }}
      />
      
      {/* Hero Section */}
      <section className="relative h-screen flex items-center">
        <div className="absolute inset-0">
          <video 
            src="/videos/1080 2024 portfolio video.mp4" 
            aria-label="Cosmik Drones Portfolio Showcase for Lehigh Valley, PA" 
            className="w-full h-full object-cover"
            autoPlay
            muted
            loop
            playsInline
          />
        </div>
        
        <div className="section-container relative z-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-lightText mb-6 leading-tight">
              Lehigh Valley's Premier <span className="text-primary">Drone</span> Services
            </h1>
            <p className="text-xl text-lightText/80 mb-8">
              Professional aerial cinematography and FPV drone services in Lehigh Valley, Pennsylvania.
              Serving Allentown, Bethlehem, Easton, and throughout Eastern PA with stunning aerial perspectives.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/services"
                className="bg-gradient-cosmic hover:shadow-cosmic-glow text-lightText font-bold py-3 px-8 rounded-md text-lg transition-all hover-lift inline-block text-center"
                aria-label="View drone services in Lehigh Valley"
              >
                My Services
              </Link>
              <Link
                to="/portfolio"
                className="bg-transparent hover:bg-lightText/10 text-lightText border border-secondary/30 font-bold py-3 px-8 rounded-md text-lg transition-colors inline-block text-center hover-lift"
                aria-label="View drone portfolio for Pennsylvania projects"
              >
                View Portfolio
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Phantoms Hockey Video Section */}
      <section className="relative h-screen">
        <div className="absolute inset-0">
          <video 
            src="/videos/Phantoms website video.mp4" 
            aria-label="Lehigh Valley Phantoms Hockey drone footage"
            className="w-full h-full object-cover"
            autoPlay
            muted
            loop
            playsInline
          />
        </div>
        <div className="absolute inset-0 flex items-center z-20">
          <div className="section-container">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="max-w-2xl"
            >
              <h2 className="text-4xl md:text-5xl font-bold text-lightText mb-6 leading-tight">
                CAPTURING <span className="text-primary">ACTION</span> IN PENNSYLVANIA
              </h2>
              <p className="text-xl text-lightText/80 mb-8">
                Professional drone videography for sporting events, concerts, and outdoor activities throughout the Lehigh Valley and Eastern Pennsylvania.
              </p>
              <Link
                to="/services"
                className="bg-gradient-cosmic hover:shadow-cosmic-glow text-lightText font-bold py-3 px-8 rounded-md text-lg transition-all hover-lift inline-block"
                aria-label="Book drone services for your event in Pennsylvania"
              >
                Book Your Event
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Local Service Areas Section - NEW SECTION */}
      <section className="py-20 bg-gradient-to-b from-darkBg to-darkBg/80 relative">
        <div className="section-container relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold text-lightText mb-4">Serving the <span className="text-secondary">Lehigh Valley</span> & Beyond</h2>
            <p className="text-xl text-lightText/80 max-w-3xl mx-auto">
              Professional drone services available throughout Eastern Pennsylvania and neighboring states.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <FeatureCard
              title="Allentown"
              description="Premium drone services for Allentown's urban landscape, real estate, and corporate events."
              icon={<span className="text-4xl">🏙️</span>}
              delay={0}
            />
            <FeatureCard
              title="Bethlehem"
              description="Capturing Bethlehem's historic charm and modern developments from breathtaking perspectives."
              icon={<span className="text-4xl">🏛️</span>}
              delay={0.1}
            />
            <FeatureCard
              title="Easton"
              description="Aerial photography and videography highlighting Easton's scenic Delaware riverfront and vibrant community."
              icon={<span className="text-4xl">🌉</span>}
              delay={0.2}
            />
            <FeatureCard
              title="Greater Pennsylvania"
              description="Extending services to Philadelphia, Harrisburg, and throughout the East Coast for special projects."
              icon={<span className="text-4xl">🗺️</span>}
              delay={0.3}
            />
          </div>
          
          {/* Removed Allentown Statue Image */}
        </div>
      </section>

      {/* PD far image - NEW */}
      <div className="mb-16">
        <div className="group relative rounded-lg overflow-hidden shadow-lg hover-lift">
          <img 
            src="/images/PD Far.jpg" 
            alt="Drone aerial photography" 
            className="w-full object-cover object-top h-[500px]" 
          />
          <div className="absolute inset-0 bg-gradient-to-t from-darkBg to-transparent opacity-50"></div>
        </div>
      </div>

      {/* Services Preview Section */}
      <section className="py-24 bg-gradient-to-b from-darkBg via-darkBg/80 to-secondary/70 relative">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-1/4 right-10 w-72 h-72 rounded-full bg-secondary/20 filter blur-3xl"></div>
          <div className="absolute bottom-1/4 left-10 w-64 h-64 rounded-full bg-secondary/20 filter blur-3xl"></div>
        </div>
        <div className="section-container relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-lightText mb-4">My <span className="text-secondary">Services</span></h2>
            <p className="text-xl text-lightText/80 max-w-3xl mx-auto">
              From stunning aerial photography to dynamic FPV drone cinematography, I deliver professional results tailored to your vision.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <ServiceCard 
              title="Aerial Photography"
              description="High-resolution aerial imagery that showcases properties, landscapes, and events from unique perspectives."
              icon={
                <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              }
              delay={0}
            />
            <ServiceCard 
              title="FPV Drone Filming"
              description="Immersive first-person-view drone footage that creates dynamic, cinematic sequences with impossible perspectives."
              icon={
                <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              }
              delay={0.2}
            />
            <ServiceCard 
              title="Real Estate & Events"
              description="Specialized aerial services for real estate marketing, construction documentation, and event coverage."
              icon={
                <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              }
              delay={0.4}
            />
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="text-center mt-12"
          >
            <Link
              to="/services"
              className="inline-flex items-center text-primary font-bold text-lg group hover-lift"
            >
              View All Services
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                className="h-5 w-5 ml-2 transform group-hover:translate-x-1 transition-transform" 
                viewBox="0 0 20 20" 
                fill="currentColor"
              >
                <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* My Equipment Section - NEW */}
      <section className="py-24 bg-gradient-to-b from-secondary/70 via-darkBg/90 to-darkBg relative">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-1/3 left-10 w-80 h-80 rounded-full bg-primary/15 filter blur-3xl"></div>
          <div className="absolute bottom-1/3 right-10 w-72 h-72 rounded-full bg-secondary/15 filter blur-3xl"></div>
        </div>
        <div className="section-container relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-lightText mb-4">Professional <span className="text-primary">Equipment</span></h2>
            <p className="text-xl text-lightText/80 max-w-3xl mx-auto">
              I use state-of-the-art drones and equipment to deliver exceptional results for every project.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            <DroneCard
              title="Cinelog 30"
              description="Powerful FPV cinewhoop for indoor and tight-space filming with HD video capabilities."
              image="/images/Drone/Cinelog30.png"
              delay={0.1}
            />
            <DroneCard
              title="Mavic 2 Pro"
              description="Professional-grade aerial photography drone with 1-inch sensor and Hasselblad camera."
              image="/images/Drone/Mavic2.png"
              delay={0.2}
            />
            <DroneCard
              title="FPV Freestyle"
              description="Custom-built high-performance FPV drone for dynamic and action shots."
              image="/images/Drone/IMG_20230417_144550_120.jpg"
              delay={0.3}
            />
            <DroneCard
              title="Skyeliner"
              description="Long-range cinematic drone for extended flight times and sweeping landscape shots."
              image="/images/Drone/Skyeliner.png"
              delay={0.4}
            />
            <DroneCard
              title="Squirt v2"
              description="Ultra-small FPV drone perfect for capturing unique perspectives in tight spaces."
              image="/images/Drone/Squirt v2.png"
              delay={0.5}
            />
            <DroneCard
              title="Professional Setup"
              description="Complete professional control systems and monitoring equipment for precise flight control."
              image="/images/Drone photo.jpg"
              delay={0.6}
            />
          </div>

          <div className="text-center">
            <Link
              to="/contact"
              className="bg-gradient-cosmic hover:shadow-cosmic-glow text-lightText font-bold py-3 px-8 rounded-md text-lg transition-all hover-lift inline-block"
            >
              Ask About My Equipment
            </Link>
          </div>
        </div>
      </section>

      {/* Portfolio Showcase */}
      <section className="py-24 bg-gradient-to-b from-darkBg to-darkBg relative">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-1/3 left-10 w-80 h-80 rounded-full bg-primary/15 filter blur-3xl"></div>
          <div className="absolute bottom-1/3 right-10 w-72 h-72 rounded-full bg-secondary/15 filter blur-3xl"></div>
        </div>
        <div className="section-container relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-lightText mb-4">Featured <span className="text-secondary">Projects</span></h2>
            <p className="text-xl text-lightText/80 max-w-3xl mx-auto">
              Explore my portfolio of drone cinematography across various industries and environments.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <div className="overflow-hidden rounded-lg relative group cursor-pointer" onClick={() => window.location.href = "/portfolio?project=0"}>
              <div className="aspect-video">
                <WebsitePreview 
                  site="discover-lehigh-valley"
                  onClick={() => window.location.href = "/portfolio?project=0"}
                  delay={0.1}
                />
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-6 z-20">
                <span className="text-secondary text-sm font-medium">Tourism Campaign</span>
                <h3 className="text-lightText text-xl font-bold transform translate-y-8 group-hover:translate-y-0 transition-transform">Discover Lehigh Valley</h3>
                <div className="mt-3 opacity-0 group-hover:opacity-100 transition-opacity flex items-center">
                  <span className="text-lightText text-sm">View Details</span>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1 text-lightText" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
            </div>
            
            <div className="overflow-hidden rounded-lg relative group cursor-pointer" onClick={() => openExternalLink("https://www.jtwo.tv/portfolio/pa-gets-it-done-2/")}>
              <div className="aspect-video">
                <WebsitePreview 
                  site="pa-gets-it-done"
                  onClick={() => openExternalLink("https://www.jtwo.tv/portfolio/pa-gets-it-done-2/")}
                  delay={0.2}
                />
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-6 z-20">
                <span className="text-secondary text-sm font-medium">Official Campaign</span>
                <h3 className="text-lightText text-xl font-bold transform translate-y-8 group-hover:translate-y-0 transition-transform">PA Gets It Done</h3>
                <div className="mt-3 opacity-0 group-hover:opacity-100 transition-opacity flex items-center">
                  <span className="text-lightText text-sm">Visit Project</span>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1 text-lightText" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="text-center mt-12"
          >
            <Link
              to="/portfolio"
              className="inline-flex items-center text-primary font-bold text-lg group hover-lift"
            >
              View Full Portfolio
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                className="h-5 w-5 ml-2 transform group-hover:translate-x-1 transition-transform" 
                viewBox="0 0 20 20" 
                fill="currentColor"
              >
                <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Why Choose Us Section - Updated with filler images */}
      <section className="py-24 bg-transparent relative">
        <div className="section-container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-lightText mb-4">Why Choose Me</h2>
            <p className="text-xl text-lightText/80 max-w-3xl mx-auto">
              My commitment to excellence, creative vision, and technical expertise sets me apart.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-16 items-center">
            <div 
              className="relative h-[400px] rounded-lg overflow-hidden cursor-pointer group"
              onClick={() => openExternalLink("https://www.jtwo.tv/portfolio/pa-gets-it-done-2/")}
            >
              <div className="h-full">
                <WebsitePreview 
                  site="pa-gets-it-done"
                  onClick={() => openExternalLink("https://www.jtwo.tv/portfolio/pa-gets-it-done-2/")}
                  delay={0.3}
                  className="h-full"
                />
              </div>
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-darkBg/50 px-4 py-2 rounded text-center">
                <h3 className="text-lightText text-xl font-bold">PA Gets It Done Campaign</h3>
                <p className="text-lightText text-sm mt-1">Click to view project</p>
              </div>
            </div>
            <div>
              <h3 className="text-2xl font-bold text-lightText mb-6">Featured Government Project</h3>
              <p className="text-lg text-lightText/80 mb-6">
                I was honored to contribute to the "PA Gets It Done" campaign for the Pennsylvania Commonwealth, which showcases the state's innovative industries and business-friendly environment.
              </p>
              <p className="text-lg text-lightText/80">
                Working alongside Red House Communications and JTWO, my drone cinematography helped capture the essence of Pennsylvania's agriculture, energy, life sciences, manufacturing, and technology sectors across the state.
              </p>
              <button 
                onClick={() => openExternalLink("https://www.jtwo.tv/portfolio/pa-gets-it-done-2/")} 
                className="mt-6 bg-primary/20 hover:bg-primary/30 text-lightText font-medium py-2 px-4 rounded-md text-base transition-colors inline-flex items-center"
              >
                View Full Project
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            <FeatureCard
              title="FAA Certified"
              description="I hold current Part 107 certification for commercial drone operations."
              icon={
                <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              }
              delay={0}
            />
            <FeatureCard
              title="State-of-the-Art Gear"
              description="Professional equipment including DJI and custom FPV drones for versatile filming options."
              icon={
                <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              }
              delay={0.2}
            />
            <FeatureCard
              title="Creative Excellence"
              description="Artistic vision combined with technical skill to capture stunning aerial perspectives."
              icon={
                <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              }
              delay={0.4}
            />
            <FeatureCard
              title="Quick Turnaround"
              description="Efficient workflow and professional editing for timely project delivery."
              icon={
                <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              }
              delay={0.6}
            />
          </div>
        </div>
      </section>

      {/* Client Logos Section */}
      <section className="py-20 bg-gradient-to-b from-darkBg to-darkBg/90 relative">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full bg-secondary/5 filter blur-3xl animate-pulse-slow"></div>
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full bg-secondary/5 filter blur-3xl animate-pulse-slow"></div>
        </div>
        <div className="section-container relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-lightText mb-4">Trusted By <span className="text-secondary">Industry Leaders</span></h2>
            <p className="text-xl text-lightText/80 max-w-3xl mx-auto">
              I'm proud to have collaborated with these innovative companies and organizations to deliver exceptional aerial content.
            </p>
          </motion.div>

          {/* Featured Full-Width Photo */}
          <div className="mb-16">
            <div className="group relative rounded-lg overflow-hidden shadow-lg hover-lift">
              <img src="/images/Art museum (3 of 4).jpg" alt="Philadelphia Art Museum" className="w-full object-cover h-[500px]" />
              <div className="absolute inset-0 bg-gradient-to-t from-darkBg to-transparent opacity-70"></div>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            {/* Client Logo Carousel */}
            <div className="logos-container relative">
              {/* First Row - Premium Clients */}
              <div className="mb-16">
                <h3 className="text-xl font-bold text-lightText mb-8 text-center">Featured Collaborations</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
                  <ClientLogo 
                    name="MLB" 
                    logo="/logos/MLB-Logo.png"
                    index={0}
                    description="Major League Baseball content partnership"
                    featured={true}
                  />
                  <ClientLogo 
                    name="FanDuel" 
                    logo="/logos/Fanduel.png"
                    index={1}
                    description="Dynamic event coverage and promotional content"
                    featured={true}
                  />
                  <ClientLogo 
                    name="Home Depot" 
                    logo="/logos/The_Home_Depot_logo-4000x4000.png"
                    index={2}
                    description="Commercial and promotional campaigns"
                    featured={true}
                  />
                </div>
              </div>
              
              {/* Second Row - Other Clients */}
              <div>
                <h3 className="text-xl font-bold text-lightText mb-8 text-center">Additional Partners</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
                  <ClientLogo 
                    name="Lehigh Valley Phantoms" 
                    logo="/logos/Phantoms.png"
                    index={3} 
                  />
                  <ClientLogo 
                    name="Reading Royals" 
                    logo="/logos/Reading_Royals_logo.svg.png"
                    index={4} 
                  />
                  <ClientLogo 
                    name="Sam's Club" 
                    logo="/logos/Sams_Club.png"
                    index={5} 
                  />
                  <ClientLogo 
                    name="Four Seasons" 
                    logo="/logos/Four-Seasons-Logo.png"
                    index={6} 
                  />
                  <ClientLogo 
                    name="Beats by Dre" 
                    logo="/logos/Beats-by-Dre-logo.png"
                    index={7} 
                  />
                  <ClientLogo 
                    name="Penn Museum" 
                    logo="/logos/Penn-Museum-Logo.png"
                    index={8} 
                  />
                  <ClientLogo 
                    name="Union League" 
                    logo="/logos/Union League.png"
                    index={9} 
                  />
                  <ClientLogo 
                    name="Skirmish" 
                    logo="/logos/Skirmish small trans.png"
                    index={10} 
                  />
                  <ClientLogo 
                    name="Hangdog" 
                    logo="/logos/Hangdog-removebg-preview.png"
                    index={11} 
                  />
                  <ClientLogo 
                    name="Bikes for Charity" 
                    logo="/logos/B4C_Square_Logo.jpg"
                    index={12} 
                  />
                </div>
              </div>
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-16 bg-gradient-to-r from-darkCard/80 to-secondary/20 p-8 rounded-xl"
          >
            <div className="flex flex-col md:flex-row items-center justify-between">
              <div className="md:w-2/3 mb-6 md:mb-0 md:pr-6">
                <h3 className="text-2xl font-bold text-lightText mb-3">Ready to join these industry leaders?</h3>
                <p className="text-lightText">
                  Let's discuss how my drone services can help your next project stand out from the competition.
                </p>
              </div>
              <div>
                <a 
                  href="/contact" 
                  className="bg-primary hover:bg-primary/90 text-darkBg font-bold py-3 px-8 rounded-md text-base transition-colors inline-block"
                >
                  Get in Touch
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Instagram Feed Section - Updated */}
      <section className="py-20 bg-darkBg relative">
        <div className="absolute inset-0 opacity-5">
          {/* Subtle background elements if desired */}
        </div>
        <div className="section-container relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold text-lightText mb-4">Follow My Work on <span className="text-primary">Instagram</span></h2>
            <p className="text-xl text-lightText/80 max-w-3xl mx-auto">
              See the latest aerial captures and behind-the-scenes from Cosmik Drones.
            </p>
          </motion.div>

          <InstagramFeed />

        </div>
      </section>

      {/* Call to Action */}
      <section className="py-24 bg-gradient-to-r from-secondary/20 to-primary/20">
        <div className="section-container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h2 className="text-4xl font-bold text-lightText mb-6">Ready to Elevate Your Project?</h2>
            <p className="text-xl text-lightText/80 max-w-3xl mx-auto mb-10">
              Contact me today to discuss how my drone services can meet your aerial imaging needs.
            </p>
            <Link
              to="/contact"
              className="bg-primary hover:bg-primary/90 text-darkBg font-bold py-4 px-10 rounded-md text-lg transition-colors inline-block"
            >
              Get a Quote
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

// Service Card Component
const ServiceCard = ({ title, description, icon, delay = 0 }) => (
  <motion.div 
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.6, delay }}
    className="glass-card-cosmic rounded-lg p-8 flex flex-col items-center text-center hover-lift hover-cosmic-glow transition-all"
  >
    <div className="text-secondary mb-6 animate-pulse-slow">{icon}</div>
    <h3 className="text-xl font-bold text-lightText mb-3">{title}</h3>
    <p className="text-lightText">{description}</p>
  </motion.div>
);

// Feature Card Component
const FeatureCard = ({ title, description, icon, delay = 0, imageSrc }) => (
  <motion.div 
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.6, delay }}
    className="glass-card-cosmic p-6 rounded-lg hover-lift h-full flex flex-col"
  >
    {imageSrc ? (
      <div className="w-full h-40 mb-4 rounded-md overflow-hidden">
        <img src={imageSrc} alt={title} className="w-full h-full object-cover" />
      </div>
    ) : (
      <div className="text-secondary mb-4">{icon}</div>
    )}
    <h3 className="text-xl font-bold text-lightText mb-2">{title}</h3>
    <p className="text-lightText flex-grow">{description}</p>
  </motion.div>
);

// Portfolio Card Component
const PortfolioCard = ({ title, category, image, video, delay = 0, externalLink, onClick, link }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.6, delay }}
    className="overflow-hidden rounded-lg relative group hover-lift cursor-pointer"
    onClick={onClick || (link ? () => window.location.href = link : null)}
  >
    <div className="absolute inset-0 bg-gradient-to-t from-secondary/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity z-10"></div>
    
    {video ? (
      <video 
        src={video} 
        className="w-full h-80 object-cover transition-transform duration-500 group-hover:scale-105"
        autoPlay
        muted
        loop
        playsInline
      />
    ) : (
      <img src={image} alt={title} className="w-full h-80 object-cover transition-transform duration-500 group-hover:scale-105" />
    )}
    
    <div className="absolute bottom-0 left-0 right-0 p-6 z-20">
      <span className="text-secondary text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">{category}</span>
      <h3 className="text-lightText text-xl font-bold transform translate-y-8 group-hover:translate-y-0 transition-transform">{title}</h3>
      
      {externalLink && (
        <div className="mt-3 opacity-0 group-hover:opacity-100 transition-opacity flex items-center">
          <span className="text-lightText text-sm">Visit Project</span>
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1 text-lightText" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
          </svg>
        </div>
      )}
      
      {link && (
        <div className="mt-3 opacity-0 group-hover:opacity-100 transition-opacity flex items-center">
          <span className="text-lightText text-sm">View Details</span>
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1 text-lightText" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </div>
      )}
    </div>
  </motion.div>
);

// Client Logo Component
const ClientLogo = ({ name, logo, index, delay = 0, description = "", featured = false }) => {
  // This array of background gradient classes ensures each logo has a unique background
  // This helps with visibility for different colored logos
  const bgClasses = [
    'from-darkCard/40 to-secondary/10',
    'from-darkCard/40 to-primary/10',
    'from-darkCard/40 to-darkBg/20',
    'from-secondary/10 to-primary/10',
    'from-darkCard/30 to-secondary/5',
  ];

  // Additional unique color combinations for standout logos
  const featuredBgClasses = [
    'from-darkCard/60 via-secondary/20 to-primary/25',
    'from-darkCard/60 via-primary/20 to-secondary/25',
  ];

  const chosenBg = featured ? 
    featuredBgClasses[index % featuredBgClasses.length] : 
    bgClasses[index % bgClasses.length];

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: delay + index * 0.05 }}
      className={`p-6 rounded-lg flex flex-col items-center justify-center bg-gradient-to-br ${chosenBg} ${featured ? 'shadow-lg' : 'shadow-md'} hover:shadow-lg transition-all duration-300 h-full group`}
    >
      <div className={`bg-white rounded-lg ${featured ? 'p-5' : 'p-4'} flex items-center justify-center w-full ${featured ? 'h-32' : 'h-28'} shadow-sm group-hover:shadow-md transition-all ${featured ? 'border border-secondary/20' : ''} group-hover:border-secondary/30`}>
            <img 
              src={logo} 
              alt={`${name} logo`} 
          className={`${featured ? 'max-h-24' : 'max-h-20'} max-w-full object-contain group-hover:scale-110 transition-transform duration-300`}
        />
          </div>
            {description && (
        <p className={`${featured ? 'text-sm' : 'text-xs'} text-lightText/80 mt-4 text-center group-hover:text-lightText transition-colors`}>{description}</p>
            )}
      <p className="text-xs text-lightText/60 mt-1 opacity-0 group-hover:opacity-100 transition-opacity">{name}</p>
    </motion.div>
  );
};

// Instagram Feed Component
const InstagramFeed = () => {
  // For local development, you might need to run a CORS proxy if Instagram blocks requests.
  // Example: https://cors-anywhere.herokuapp.com/https://www.instagram.com/cosmik.drones/
  // IMPORTANT: For production, it's better to use Instagram's official API or a server-side solution.
  const profileUrl = "https://www.instagram.com/josh.kolonia/";

    return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 lg:gap-6">
      <motion.div initial={{ opacity: 0, y:20 }} whileInView={{ opacity:1, y:0 }} transition={{ delay: 0.1}} className="aspect-square bg-darkCard rounded-lg overflow-hidden hover-lift">
        <InstagramEmbed url={`${profileUrl}reel/DJmd0eexkzi/`} width="100%" height="100%" captioned />
      </motion.div>
      <motion.div initial={{ opacity: 0, y:20 }} whileInView={{ opacity:1, y:0 }} transition={{ delay: 0.2}} className="aspect-square bg-darkCard rounded-lg overflow-hidden hover-lift">
        <InstagramEmbed url={`${profileUrl}reel/DJjfZJBxDVj/`} width="100%" height="100%" captioned />
      </motion.div>
      <motion.div initial={{ opacity: 0, y:20 }} whileInView={{ opacity:1, y:0 }} transition={{ delay: 0.3}} className="aspect-square bg-darkCard rounded-lg overflow-hidden hover-lift">
        <InstagramEmbed url={`${profileUrl}reel/DI84oIKx2T9/`} width="100%" height="100%" captioned />
      </motion.div>
      <motion.div initial={{ opacity: 0, y:20 }} whileInView={{ opacity:1, y:0 }} transition={{ delay: 0.4}} className="aspect-square bg-darkCard rounded-lg overflow-hidden hover-lift">
        <InstagramEmbed url={`${profileUrl}reel/DG6VAUPRQpj/`} width="100%" height="100%" captioned />
      </motion.div>
      <motion.div initial={{ opacity: 0, y:20 }} whileInView={{ opacity:1, y:0 }} transition={{ delay: 0.5}} className="aspect-square bg-darkCard rounded-lg overflow-hidden hover-lift">
        <InstagramEmbed url={`${profileUrl}reel/DCmK6M7x1K6/`} width="100%" height="100%" captioned />
      </motion.div>
      <motion.div initial={{ opacity: 0, y:20 }} whileInView={{ opacity:1, y:0 }} transition={{ delay: 0.6}} className="aspect-square bg-darkCard rounded-lg overflow-hidden hover-lift">
        <InstagramEmbed url={`${profileUrl}reel/DAvv4y9PWi2/`} width="100%" height="100%" captioned />
      </motion.div>
    </div>
  );
};

// Add this new component for the Equipment section
const DroneCard = ({ title, description, image, delay = 0 }) => (
  <motion.div 
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.6, delay }}
    className="glass-card-cosmic rounded-lg overflow-hidden hover-lift hover-cosmic-glow transition-all h-full flex flex-col"
  >
    <div className="h-56 overflow-hidden">
        <img 
          src={image} 
        alt={title} 
        className="w-full h-full object-cover object-center transition-transform duration-500 hover:scale-105"
      />
          </div>
    <div className="p-6 flex flex-col flex-grow">
      <h3 className="text-xl font-bold text-lightText mb-2">{title}</h3>
      <p className="text-lightText/80 flex-grow">{description}</p>
        </div>
  </motion.div>
);

export default Home; 